import React, { useState } from "react";
import { Check, Users, Sparkles, Calendar } from "lucide-react";
import { toast } from "sonner";
import { MEMBERSHIP_TIERS, IMAGES } from "../data/content";
import { Reveal } from "../components/Primitives";
import { subscribe } from "../lib/api";

const perks = [
  { icon: Users, title: "Live Circles", text: "Monthly gatherings to connect, reflect, and grow alongside kindred spirits." },
  { icon: Sparkles, title: "Member Library", text: "Full access to workbooks, courses, and guided practices in one calm place." },
  { icon: Calendar, title: "Group Learning", text: "Cohort-based programs and challenges to keep you accountable and inspired." },
];

export default function Community() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onJoin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await subscribe({ email });
      toast.success(res.message || "Welcome to the community.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="community-page">
      {/* HERO */}
      <section className="relative pt-36 pb-24 md:pt-44 md:pb-32 overflow-hidden grain">
        <img src={IMAGES.meditationGroup} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#120726] via-[#120726]/90 to-[#120726]/50" />
        <div className="container-loft relative z-10 max-w-2xl">
          <Reveal>
            <p className="overline mb-5">The Loft Community</p>
            <h1 className="font-serif text-5xl md:text-7xl font-light leading-[1.03] text-[#EFE7FA]">
              Grow in good <span className="text-gradient">company</span>
            </h1>
            <p className="mt-7 text-lg md:text-xl font-light leading-relaxed text-[#EFE7FA]/75">
              Healing and building are lighter when shared. Step into a warm circle of intentional
              people learning, growing, and rising together.
            </p>
          </Reveal>
        </div>
      </section>

      {/* PERKS */}
      <section className="py-20 md:py-28 bg-[#1a0d33]">
        <div className="container-loft grid md:grid-cols-3 gap-8">
          {perks.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.1}>
              <div className="card-soft p-9 h-full">
                <div className="h-12 w-12 rounded-full bg-[#5B2C83]/10 grid place-items-center mb-5">
                  <p.icon size={22} className="text-[#C4A2E8]" />
                </div>
                <h3 className="font-serif text-2xl text-[#EFE7FA]">{p.title}</h3>
                <p className="mt-3 text-[#EFE7FA]/70 font-light leading-relaxed">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* MEMBERSHIP TIERS */}
      <section className="py-24 md:py-32 bg-[#120726]">
        <div className="container-loft">
          <Reveal>
            <p className="overline mb-4 text-center">Membership</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-center text-[#EFE7FA]">Choose your circle</h2>
            <p className="mt-4 text-center text-[#EFE7FA]/70 font-light max-w-xl mx-auto">
              Begin where you are. Every tier opens the door to deeper support and community.
            </p>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-8 mt-14 items-stretch">
            {MEMBERSHIP_TIERS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.1}>
                <div
                  data-testid={`membership-tier-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`relative h-full rounded-[1.5rem] p-9 flex flex-col transition-all duration-500 ${
                    t.highlight
                      ? "bg-[#231233] text-white shadow-[0_30px_70px_rgba(35,18,51,0.35)] md:-translate-y-4"
                      : "card-soft"
                  }`}
                >
                  {t.highlight && (
                    <span className="absolute top-5 right-5 bg-[#A8D8F0] text-[#231233] text-xs tracking-wide px-3 py-1 rounded-full">Most Loved</span>
                  )}
                  <h3 className={`font-serif text-2xl ${t.highlight ? "text-white" : "text-[#EFE7FA]"}`}>{t.name}</h3>
                  <div className="mt-4 flex items-baseline gap-1">
                    <span className={`font-serif text-5xl font-light ${t.highlight ? "text-[#A8D8F0]" : "text-[#C4A2E8]"}`}>{t.price}</span>
                    <span className={`text-sm ${t.highlight ? "text-white/60" : "text-[#EFE7FA]/50"}`}>{t.period}</span>
                  </div>
                  <ul className="mt-7 space-y-3 flex-1">
                    {t.perks.map((perk) => (
                      <li key={perk} className={`flex items-start gap-3 text-sm font-light ${t.highlight ? "text-white/80" : "text-[#EFE7FA]/75"}`}>
                        <Check size={16} className={t.highlight ? "text-[#A8D8F0] mt-0.5 shrink-0" : "text-[#0B7A5A] mt-0.5 shrink-0"} />
                        {perk}
                      </li>
                    ))}
                  </ul>
                  <button
                    data-testid={`membership-cta-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => toast.success("Memberships open soon — you'll be first to know!")}
                    className={`mt-8 ${t.highlight ? "btn-primary bg-[#5B2C83]" : "btn-ghost"} w-full`}
                  >
                    {t.cta}
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* JOIN CTA */}
      <section className="py-24 bg-[#1a0d33]">
        <div className="container-loft">
          <Reveal>
            <div className="card-soft p-10 md:p-16 text-center max-w-3xl mx-auto grain relative">
              <div className="glow-ice absolute -top-10 left-1/2 -translate-x-1/2 w-48 h-48 rounded-full opacity-50" />
              <div className="relative z-10">
                <h2 className="font-serif text-3xl md:text-4xl font-light text-[#EFE7FA]">Be first through the doors</h2>
                <p className="mt-4 text-[#EFE7FA]/70 font-light">
                  Join the waitlist and we'll welcome you the moment the community opens.
                </p>
                <form onSubmit={onJoin} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="community-join-form">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    data-testid="community-join-input"
                    className="flex-1 bg-white/5 border border-[#C0C0C0]/50 rounded-full px-5 py-3 text-[#EFE7FA] placeholder:text-[#EFE7FA]/40 focus:outline-none focus:border-[#5B2C83] transition-colors"
                  />
                  <button type="submit" disabled={loading} data-testid="community-join-button" className="btn-primary disabled:opacity-60">
                    {loading ? "Joining..." : "Join the Waitlist"}
                  </button>
                </form>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
